'use client';

export type GptSize = [number, number] | 'fluid';

declare global {
  interface Window {
    googletag?: GoogleTag;
  }
}

// Before gpt.js loads, `window.googletag` is just a command queue; the script fills in the rest.
type GoogleTag = { cmd: Array<() => void> } & Partial<GoogleTagApi>;

type GoogleTagApi = {
  defineSlot: (adUnitPath: string, sizes: ReadonlyArray<GptSize>, divId: string) => GoogleTagSlot | null;
  pubads: () => GoogleTagPubAds;
  enableServices: () => void;
  display: (divId: string) => void;
  destroySlots: (slots?: Array<GoogleTagSlot>) => boolean;
};

export type GoogleTagSlot = {
  addService: (service: GoogleTagPubAds) => void;
  getSlotElementId: () => string;
  setTargeting: (key: string, value: string | Array<string>) => void;
  clearTargeting: () => void;
};

type SlotRenderEndedEvent = {
  slot: GoogleTagSlot;
  isEmpty: boolean;
};

export type GoogleTagPubAds = {
  enableSingleRequest: () => void;
  enableAsyncRendering: () => void;
  collapseEmptyDivs: () => void;
  /** Re-requests the given slots in place, keeping their containers mounted. */
  refresh: (slots?: Array<GoogleTagSlot>) => void;
  set: (key: string, val: string) => void;
  setRequestNonPersonalizedAds: (value: 0 | 1) => void;
  setPrivacySettings: (settings: { restrictDataProcessing?: boolean }) => void;
  addEventListener: (event: 'slotRenderEnded', listener: (event: SlotRenderEndedEvent) => void) => void;
  removeEventListener: (event: 'slotRenderEnded', listener: (event: SlotRenderEndedEvent) => void) => void;
};

/**
 * Queues work against GPT, seeding the command queue if gpt.js hasn't loaded yet.
 * The callback runs once the script is ready, so the full API is available to it.
 */
export function queueGptCommand(command: (gpt: GoogleTagApi) => void): void {
  const googletag = (window.googletag ??= { cmd: [] });
  googletag.cmd.push(() => command(googletag as GoogleTagApi));
}

/** Reports whether the ad in `containerId` came back unfilled. Returns an unsubscribe. */
export function onSlotEmpty(containerId: string, onEmpty: () => void): () => void {
  let cancelled = false;
  const listener = (event: SlotRenderEndedEvent) => {
    if (event.isEmpty && event.slot.getSlotElementId() === containerId) onEmpty();
  };

  queueGptCommand((gpt) => {
    if (cancelled) return;
    gpt.pubads().addEventListener('slotRenderEnded', listener);
  });

  return () => {
    cancelled = true;
    queueGptCommand((gpt) => gpt.pubads().removeEventListener('slotRenderEnded', listener));
  };
}
